import React, { useState } from 'react';
import { 
  AlertTriangle, 
  X, 
  Phone, 
  MessageCircle, 
  MapPin, 
  Building, 
  UserX, 
  ShieldAlert, 
  Clock,
  Navigation,
  Car,
  PackageX,
  HelpCircle,
  Headphones,
  ExternalLink,
  ChevronRight,
  Shield,
  Zap
} from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { cn } from '../../lib/utils';
import { motion, AnimatePresence } from 'framer-motion';

type DeliveryProvider = 'fash' | 'uber';

interface DriverSafetyPanelProps {
  orderId: string;
  provider?: DeliveryProvider;
  onReport: (reason: string, details?: string) => void;
  onClose: () => void;
}

const safetyIssues = [
  { 
    id: 'unsafe_location', 
    label: 'Unsafe location', 
    description: 'The area feels dangerous or threatening',
    icon: MapPin, 
    color: 'text-error',
    severity: 'high'
  },
  { 
    id: 'customer_confrontation', 
    label: 'Customer confrontation', 
    description: 'Aggressive or threatening behavior',
    icon: UserX, 
    color: 'text-error',
    severity: 'high'
  },
  { 
    id: 'building_access', 
    label: 'Unable to access building', 
    description: 'Gate code not working, locked entrance',
    icon: Building, 
    color: 'text-warning',
    severity: 'medium'
  },
  { 
    id: 'store_closed', 
    label: 'Store closed/not ready', 
    description: 'Business is closed or items not prepared',
    icon: Clock, 
    color: 'text-warning',
    severity: 'medium'
  },
  { 
    id: 'customer_unavailable', 
    label: 'Customer unavailable', 
    description: 'No response after multiple attempts',
    icon: UserX, 
    color: 'text-warning',
    severity: 'medium'
  },
  { 
    id: 'wrong_address', 
    label: 'Wrong/invalid address', 
    description: 'Address does not exist or is incorrect',
    icon: Navigation, 
    color: 'text-warning',
    severity: 'medium'
  },
  { 
    id: 'vehicle_issue', 
    label: 'Vehicle/transport issue', 
    description: 'Flat tire, accident, or breakdown',
    icon: Car, 
    color: 'text-warning',
    severity: 'medium'
  },
  { 
    id: 'package_issue', 
    label: 'Package damaged/wrong', 
    description: 'Items appear damaged or incorrect',
    icon: PackageX, 
    color: 'text-amber-500',
    severity: 'low'
  },
  { 
    id: 'other', 
    label: 'Other issue', 
    description: 'Something else went wrong',
    icon: AlertTriangle, 
    color: 'text-body',
    severity: 'low'
  },
];

export function DriverSafetyPanel({ orderId, provider = 'fash', onReport, onClose }: DriverSafetyPanelProps) {
  const [selectedIssue, setSelectedIssue] = useState<string | null>(null);
  const [details, setDetails] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showProviderSupport, setShowProviderSupport] = useState(false);

  const isUber = provider === 'uber';

  const handleSubmit = async () => {
    if (!selectedIssue) return;
    
    setIsSubmitting(true);
    await onReport(selectedIssue, details || undefined);
    setIsSubmitting(false);
  };

  const selectedIssueData = safetyIssues.find(i => i.id === selectedIssue);

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 bg-black/50 flex items-end"
    >
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ type: 'spring', damping: 25 }}
        className="w-full bg-white rounded-t-3xl max-h-[90vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-100 p-4 z-10">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className={cn(
                "p-2 rounded-full",
                isUber ? "bg-gray-900" : "bg-error/10"
              )}>
                <ShieldAlert className={cn("h-5 w-5", isUber ? "text-white" : "text-error")} />
              </div>
              <div>
                <h2 className="font-serif font-bold text-heading">Report an Issue</h2>
                <div className="flex items-center space-x-2">
                  <p className="text-sm text-body">Order #{orderId.slice(-6)}</p>
                  <span className={cn(
                    "text-xs px-2 py-0.5 rounded-full font-medium",
                    isUber ? "bg-black text-white" : "bg-purple-100 text-purple-700"
                  )}>
                    {isUber ? 'Uber' : 'Fash'}
                  </span>
                </div>
              </div>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors">
              <X className="h-6 w-6 text-body" />
            </button>
          </div>
        </div>

        <div className="p-4 space-y-6">
          {/* Emergency Contact */}
          <Card className="bg-gradient-to-r from-red-50 to-orange-50 border-error/20">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="p-3 bg-error/10 rounded-full">
                    <Phone className="h-5 w-5 text-error" />
                  </div>
                  <div>
                    <p className="font-semibold text-heading">Emergency?</p>
                    <p className="text-sm text-body">Call 911 for immediate danger</p>
                  </div>
                </div>
                <Button variant="destructive" size="sm" className="shadow-lg">
                  <Phone className="h-4 w-4 mr-1" />
                  Call 911
                </Button>
              </div>
            </CardContent>
          </Card>

          {/* Provider-specific support */}
          {isUber && (
            <Card className="bg-gray-900 border-0">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-white/10 rounded-full">
                      <Headphones className="h-5 w-5 text-white" />
                    </div>
                    <div>
                      <p className="font-semibold text-white">Uber Support</p>
                      <p className="text-sm text-gray-400">Get help from Uber directly</p>
                    </div>
                  </div>
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="border-white/20 text-white hover:bg-white/10"
                    onClick={() => setShowProviderSupport(true)}
                  >
                    <ExternalLink className="h-4 w-4 mr-1" />
                    Open
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Quick Actions */}
          <div>
            <h3 className="font-medium text-heading mb-3">Quick Actions</h3>
            <div className="grid grid-cols-3 gap-2">
              <button className="flex flex-col items-center justify-center p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                <Phone className="h-5 w-5 text-body mb-1" />
                <span className="text-xs text-body">Call Support</span>
              </button>
              <button className="flex flex-col items-center justify-center p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                <MessageCircle className="h-5 w-5 text-body mb-1" />
                <span className="text-xs text-body">Live Chat</span>
              </button>
              <button className="flex flex-col items-center justify-center p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors">
                <HelpCircle className="h-5 w-5 text-body mb-1" />
                <span className="text-xs text-body">Help Center</span>
              </button>
            </div>
          </div>

          {/* Issue Selection */}
          <div>
            <h3 className="font-medium text-heading mb-3">What's the issue?</h3>
            <div className="space-y-2">
              {safetyIssues.map((issue) => {
                const Icon = issue.icon;
                const isSelected = selectedIssue === issue.id;
                
                return (
                  <button
                    key={issue.id}
                    onClick={() => setSelectedIssue(issue.id)}
                    className={cn(
                      "w-full flex items-center space-x-3 p-4 rounded-xl border-2 transition-all",
                      isSelected 
                        ? isUber
                          ? "border-gray-900 bg-gray-50" 
                          : "border-primary bg-primary/5"
                        : "border-gray-100 hover:border-gray-200 hover:bg-gray-50"
                    )}
                  >
                    <div className={cn(
                      "w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0",
                      issue.severity === 'high' 
                        ? "bg-error/10" 
                        : issue.severity === 'medium'
                          ? "bg-warning/10"
                          : "bg-gray-100"
                    )}>
                      <Icon className={cn("h-5 w-5", issue.color)} />
                    </div>
                    <div className="flex-1 text-left">
                      <span className={cn(
                        "font-medium block",
                        isSelected 
                          ? isUber ? "text-gray-900" : "text-primary" 
                          : "text-heading"
                      )}>
                        {issue.label}
                      </span>
                      <span className="text-sm text-body">{issue.description}</span>
                    </div>
                    {issue.severity === 'high' && (
                      <span className="px-2 py-0.5 bg-error/10 text-error text-xs font-medium rounded-full">
                        Urgent
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Additional Details */}
          <AnimatePresence>
            {selectedIssue && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
              >
                <label className="block">
                  <span className="font-medium text-heading mb-2 block">Additional details (optional)</span>
                  <textarea
                    value={details}
                    onChange={(e) => setDetails(e.target.value)}
                    placeholder="Describe the situation in more detail..."
                    rows={3}
                    className="w-full rounded-xl border border-gray-200 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary resize-none"
                  />
                </label>

                {/* Photo upload option */}
                <div className="mt-3 p-3 bg-gray-50 rounded-xl">
                  <button className="w-full flex items-center justify-between text-left">
                    <div className="flex items-center space-x-3">
                      <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center border border-gray-200">
                        <Shield className="h-5 w-5 text-body" />
                      </div>
                      <div>
                        <p className="font-medium text-heading">Add photo evidence</p>
                        <p className="text-sm text-body">Optional but helps resolve faster</p>
                      </div>
                    </div>
                    <ChevronRight className="h-5 w-5 text-body" />
                  </button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Actions */}
          <div className="space-y-3 pb-6">
            <Button
              fullWidth
              onClick={handleSubmit}
              disabled={!selectedIssue}
              loading={isSubmitting}
              className={cn(
                isUber 
                  ? "bg-gray-900 hover:bg-black" 
                  : "bg-heading hover:bg-black"
              )}
            >
              Submit Report
            </Button>
            
            <div className="flex space-x-3">
              <Button 
                variant="outline" 
                fullWidth 
                className="flex-1"
                onClick={() => {/* Open chat */}}
              >
                <MessageCircle className="h-4 w-4 mr-2" />
                Message Support
              </Button>
              <Button 
                variant="outline" 
                fullWidth 
                className="flex-1"
                onClick={() => {/* Call support */}}
              >
                <Phone className="h-4 w-4 mr-2" />
                Call Support
              </Button>
            </div>

            {/* Uber specific - in-app navigation to safety toolkit */}
            {isUber && (
              <button className="w-full py-3 text-sm text-body hover:text-heading transition-colors flex items-center justify-center space-x-2">
                <Zap className="h-4 w-4" />
                <span>Open Uber Safety Toolkit</span>
              </button>
            )}
          </div>
        </div>
      </motion.div>

      {/* Provider Support Modal */}
      <AnimatePresence>
        {showProviderSupport && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[60] bg-black/70 flex items-center justify-center p-6"
            onClick={() => setShowProviderSupport(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-white rounded-2xl p-6 max-w-sm w-full"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center">
                <div className="w-16 h-16 bg-black rounded-full flex items-center justify-center mx-auto mb-4">
                  <span className="text-white font-bold text-xl">U</span>
                </div>
                <h3 className="text-xl font-serif text-heading mb-2">Uber Support</h3>
                <p className="text-body text-sm mb-6">
                  You'll be redirected to Uber's support system for this delivery.
                </p>
                <div className="space-y-3">
                  <Button fullWidth className="bg-black hover:bg-gray-900">
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Open Uber Help
                  </Button>
                  <Button 
                    variant="outline" 
                    fullWidth
                    onClick={() => setShowProviderSupport(false)}
                  >
                    Stay Here
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}
